import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CameraIcon, VideoCameraIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { updateProfile } from '../../store/slices/userSlice';
import { fetchCurrentUser } from '../../store/slices/authSlice';
import Button from './Button';
import toast from 'react-hot-toast';

interface SpecialtyUpgradeModalProps {
  isOpen: boolean;
}

const SpecialtyUpgradeModal: React.FC<SpecialtyUpgradeModalProps> = ({ isOpen }) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const [selectedType, setSelectedType] = useState<'photographer' | 'videographer' | 'both' | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    if (!selectedType) {
      toast.error('Please select your specialty');
      return;
    }

    setSubmitting(true);
    try {
      const result = await dispatch(updateProfile({ creator_sub_type: selectedType })).unwrap();
      if (user?.id) {
        await dispatch(fetchCurrentUser(user.id));
      }
      // toast.success('Specialty updated successfully!');
    } catch (error) {
      console.error('Failed to update specialty:', error);
      toast.error('Failed to update specialty. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const options = [
    {
      value: 'photographer' as const,
      label: 'Photographer',
      icon: CameraIcon,
      description: 'I primarily shoot photos',
      color: 'blue',
    },
    {
      value: 'videographer' as const,
      label: 'Videographer',
      icon: VideoCameraIcon,
      description: 'I primarily shoot videos',
      color: 'purple',
    },
    {
      value: 'both' as const,
      label: 'Both',
      icon: SparklesIcon,
      description: 'I do both photography & videography',
      color: 'emerald',
    },
  ];

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.4, type: 'spring', stiffness: 300, damping: 25 }}
          className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl max-w-md w-full overflow-hidden relative border border-gray-100 dark:border-gray-700"
        >
          {/* Header */}
          <div className="p-8 pb-4 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-100 to-blue-100 dark:from-primary-900/30 dark:to-blue-900/30 rounded-2xl mb-5"
            >
              <CameraIcon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
            </motion.div>

            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Update Your Specialty
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
              We need to know your specialty to show you to the right clients and match you with relevant opportunities.
            </p>
          </div>

          {/* Options */}
          <div className="px-8 pb-4 space-y-3">
            {options.map((option, index) => (
              <motion.button
                key={option.value}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * (index + 1) }}
                type="button"
                onClick={() => setSelectedType(option.value)}
                className={`
                  w-full flex items-center p-4 rounded-xl border-2 transition-all duration-200 text-left
                  ${selectedType === option.value
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/20 shadow-md shadow-primary-100 dark:shadow-none'
                    : 'border-gray-100 dark:border-gray-700 hover:border-gray-200 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-750'
                  }
                `}
              >
                <div className={`
                  p-2.5 rounded-xl mr-4 transition-colors duration-200 flex-shrink-0
                  ${selectedType === option.value
                    ? `bg-${option.color}-100 dark:bg-${option.color}-900/30`
                    : 'bg-gray-100 dark:bg-gray-700'
                  }
                `}>
                  <option.icon className={`
                    w-5 h-5 transition-colors duration-200
                    ${selectedType === option.value
                      ? `text-${option.color}-600 dark:text-${option.color}-400`
                      : 'text-gray-400 dark:text-gray-500'
                    }
                  `} />
                </div>
                <div className="flex-1">
                  <h3 className={`
                    font-semibold text-sm transition-colors duration-200
                    ${selectedType === option.value
                      ? 'text-primary-700 dark:text-primary-300'
                      : 'text-gray-900 dark:text-gray-100'
                    }
                  `}>
                    {option.label}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{option.description}</p>
                </div>
                {/* Selection indicator */}
                <div className={`
                  w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ml-3 transition-all duration-200
                  ${selectedType === option.value
                    ? 'border-primary-500 bg-primary-500'
                    : 'border-gray-300 dark:border-gray-600'
                  }
                `}>
                  {selectedType === option.value && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="w-2 h-2 bg-white rounded-full"
                    />
                  )}
                </div>
              </motion.button>
            ))}
          </div>

          {/* Actions */}
          <div className="p-8 pt-4">
            <Button
              onClick={handleConfirm}
              variant="primary"
              size="lg"
              loading={submitting}
              disabled={!selectedType || submitting}
              className="w-full"
            >
              Confirm Specialty
            </Button>
            <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-4">
              You can change this later from your profile settings.
            </p>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default SpecialtyUpgradeModal;
